import type { MerchantWidgetDiagnosticDto } from "@price-rescue/api-contracts";

export type { MerchantWidgetDiagnosticDto };

export interface StorefrontWidgetConfig {
  schemaVersion: string;
  enabled: boolean;
  copy: {
    headline: string;
    body: string;
    cta: string;
  };
  eligiblePages: Array<"PRODUCT" | "CART">;
  style: {
    accentColor: string;
    textColor: string;
    headlineColor: string;
    bodyColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    borderRadiusPx: number;
  };
  resultMessages: {
    autoMatch: string;
    counteroffer: string;
    manualReview: string;
    rejected: string;
  };
}

export interface WidgetSettingsDto {
  config: StorefrontWidgetConfig;
  installation: WidgetInstallation;
  publicWidgetKey: string;
  etag: string;
  cacheControl: string;
}

type StorefrontWidgetConfigInput = Omit<
  Partial<StorefrontWidgetConfig>,
  "copy" | "style" | "resultMessages"
> & {
  copy?: Partial<StorefrontWidgetConfig["copy"]>;
  style?: Partial<StorefrontWidgetConfig["style"]>;
  resultMessages?: Partial<StorefrontWidgetConfig["resultMessages"]>;
};

export interface WidgetInstallation {
  activationStatus: "ACTIVE" | "MISSING" | "UNKNOWN";
  blockActive: "YES" | "NO" | "UNKNOWN";
  blockHandle: string;
  canonicalBlockFile: string;
  lastEventName?: string;
  lastRuntimeSeenAt?: string;
  lastRuntimeVersion?: string;
  themeEditorUrl: string;
}

export type WidgetPreviewDevice = "desktop" | "mobile";

export interface WidgetPreviewState {
  device: WidgetPreviewDevice;
  competitorUrl: string;
  customerEmail: string;
}

export const DEFAULT_WIDGET_CONFIG: StorefrontWidgetConfig = {
  schemaVersion: "2026-08-09",
  enabled: false,
  copy: {
    headline: "Found a better price?",
    body: "Send us the lower-price product URL and we will check whether we can match it.",
    cta: "Request a price match",
  },
  eligiblePages: ["PRODUCT"],
  style: {
    accentColor: "#0f766e",
    textColor: "#111827",
    headlineColor: "#111827",
    bodyColor: "#475467",
    buttonBackgroundColor: "#0f766e",
    buttonTextColor: "#ffffff",
    borderRadiusPx: 12,
  },
  resultMessages: {
    autoMatch: "Good news. We can match that price.",
    counteroffer: "We can offer a safe alternative price.",
    manualReview: "We will review this request and follow up soon.",
    rejected: "This item is not eligible for a price match.",
  },
};

export const DEFAULT_WIDGET_INSTALLATION: WidgetInstallation = {
  activationStatus: "UNKNOWN",
  blockActive: "UNKNOWN",
  blockHandle: "price-rescue-widget",
  canonicalBlockFile: "blocks/price-rescue-widget.liquid",
  themeEditorUrl: "https://example.myshopify.com/admin/themes/current/editor?template=product",
};

export function defaultWidgetPreviewState(): WidgetPreviewState {
  return {
    device: "desktop",
    competitorUrl: "https://competitor.example/products/widget",
    customerEmail: "customer@example.com",
  };
}

export function normalizeWidgetConfig(
  config: StorefrontWidgetConfigInput | undefined,
): StorefrontWidgetConfig {
  return {
    ...DEFAULT_WIDGET_CONFIG,
    ...(config ?? {}),
    copy: { ...DEFAULT_WIDGET_CONFIG.copy, ...(config?.copy ?? {}) },
    style: normalizeWidgetStyle(config?.style),
    resultMessages: { ...DEFAULT_WIDGET_CONFIG.resultMessages, ...(config?.resultMessages ?? {}) },
    schemaVersion: config?.schemaVersion ?? DEFAULT_WIDGET_CONFIG.schemaVersion,
  };
}

export function validateWidgetConfig(config: StorefrontWidgetConfig): string[] {
  return [
    validateText("Headline", config.copy.headline, 50),
    validateText("Body", config.copy.body, 140),
    validateText("CTA", config.copy.cta, 24),
    validateText("Auto-match message", config.resultMessages.autoMatch, 160),
    validateText("Counteroffer message", config.resultMessages.counteroffer, 160),
    validateText("Manual-review message", config.resultMessages.manualReview, 160),
    validateText("Rejected message", config.resultMessages.rejected, 160),
    validateColor("Headline color", config.style.headlineColor),
    validateColor("Body color", config.style.bodyColor),
    validateColor("Button color", config.style.buttonBackgroundColor),
    validateColor("Button text color", config.style.buttonTextColor),
    validateRadius(config.style.borderRadiusPx),
    config.eligiblePages.length === 0 ? "Select at least one eligible page." : undefined,
  ].filter((issue): issue is string => issue !== undefined);
}

export function sanitizeWidgetConfig(config: StorefrontWidgetConfig): StorefrontWidgetConfig {
  return {
    ...config,
    copy: {
      headline: sanitizeText(config.copy.headline),
      body: sanitizeText(config.copy.body),
      cta: sanitizeText(config.copy.cta),
    },
    style: normalizeWidgetStyle(config.style),
    resultMessages: {
      autoMatch: sanitizeText(config.resultMessages.autoMatch),
      counteroffer: sanitizeText(config.resultMessages.counteroffer),
      manualReview: sanitizeText(config.resultMessages.manualReview),
      rejected: sanitizeText(config.resultMessages.rejected),
    },
  };
}

export function normalizeWidgetStyle(
  style: Partial<StorefrontWidgetConfig["style"]> | undefined,
): StorefrontWidgetConfig["style"] {
  const buttonBackgroundColor =
    style?.buttonBackgroundColor ?? style?.accentColor ?? DEFAULT_WIDGET_CONFIG.style.accentColor;
  const headlineColor =
    style?.headlineColor ?? style?.textColor ?? DEFAULT_WIDGET_CONFIG.style.textColor;

  return {
    accentColor: buttonBackgroundColor,
    textColor: headlineColor,
    headlineColor,
    bodyColor: style?.bodyColor ?? DEFAULT_WIDGET_CONFIG.style.bodyColor,
    buttonBackgroundColor,
    buttonTextColor: style?.buttonTextColor ?? DEFAULT_WIDGET_CONFIG.style.buttonTextColor,
    borderRadiusPx: style?.borderRadiusPx ?? DEFAULT_WIDGET_CONFIG.style.borderRadiusPx,
  };
}

function validateText(label: string, value: string, maxLength: number): string | undefined {
  const sanitized = sanitizeText(value);
  return sanitized.length > 0 && sanitized.length <= maxLength
    ? undefined
    : `${label} must be between 1 and ${maxLength} safe characters.`;
}

function validateColor(label: string, value: string): string | undefined {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? undefined : `${label} must be a six-digit hex color.`;
}

function validateRadius(value: number): string | undefined {
  return Number.isInteger(value) && value >= 0 && value <= 32
    ? undefined
    : "Border radius must be an integer between 0 and 32.";
}

function sanitizeText(value: string): string {
  return value.replace(/[<>]/g, "").trim();
}
